import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { RequireOrg } from '../components/VelopulseShell';
import DraftCard from '../components/DraftCard';
import useLoad from '../utils/useLoad';
import { useAuthStore } from '../store';
import { useOrgStore } from '../store/org';
import { vpApi, errorMessage, isUnavailable } from '../api/velopulse';
import {
  assistantMsgsKey,
  assistantSessionsKey,
  loadAssistantStore,
  saveAssistantStore,
  createSession,
  switchSession,
  renameSession,
  deleteSession,
  setSessionMessages,
  currentSession,
  maxMsgSeq,
  appendSessionMessages,
  setPendingSend,
  clearPendingSend,
  getPendingSend,
} from '../utils/assistantChatStore';
import {
  assistantCapabilityCopy,
  chatItemsFromResult,
  sanitizeWebSources,
  searchMetaCopy,
  verificationLabel,
} from '../utils/assistantCapabilities';
import './assistant/assistantTools.css';
import AssistantComposerTools from './assistant/AssistantComposerTools';
import AssistantMessageExtras from './assistant/AssistantMessageExtras';
import { parseAssistantFeatures } from './assistant/assistantFeatureModel';
import {
  assistantToolErrorMessage,
  assistantFileDownloadPath,
  createAssistantArtifact,
  downloadAssistantBlob,
  generateAssistantImage,
  listAssistantConversationMessages,
  listAssistantConversations,
  loadAssistantFeatures,
  runAssistantAnalysis,
  sendAssistantMessageWithTools,
  uploadAssistantFiles,
} from './assistant/assistantToolsApi';
import {
  collectFileIds,
  enrichChatItemWithTools,
  normalizeUploadedFiles,
  ownedIdSet,
  readyAttachmentIds,
  shouldResumeResearch,
  stripUnauthorizedIdsFromText,
  toolItemsFromIndependentResult,
  visibleAttachments,
} from './assistant/assistantToolResult';

let seq = 0;
let requestSeq = 0;
const nextId = () => `m${++seq}`;

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function SessionSidebar({ store, rules, onNew, onSwitch, onRename, onDelete, onClose, onLoadOlder, canLoadOlder, loadingOlder, historyNote }) {
  const sessions = [...(store.sessions || [])].sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
  return (
    <div className="vp-asst-side">
      <div className="vp-asst-side-head">
        <h3>對話紀錄</h3>
        {onClose && <button type="button" className="vp-modal-x" aria-label="關閉對話列表" onClick={onClose}>✕</button>}
      </div>
      <button type="button" className="vp-btn primary" style={{ width: '100%', marginBottom: 12 }} onClick={onNew}>新對話</button>
      {historyNote ? <p className="vp-asst-note">{historyNote}</p> : null}
      <ul className="vp-session-list">
        {sessions.map((s) => (
          <li key={s.id}>
            <button
              type="button"
              className={`vp-session${s.id === store.currentId ? ' on' : ''}`}
              onClick={() => onSwitch(s.id)}
            >
              <strong>{s.title || '新對話'}</strong>
              <span className="vp-muted">{formatWhen(s.updatedAt)}</span>
            </button>
            <div className="vp-session-actions">
              <button type="button" onClick={() => onRename(s.id, s.title)}>重新命名</button>
              <button type="button" onClick={() => onDelete(s.id)}>刪除</button>
            </div>
          </li>
        ))}
      </ul>
      {onLoadOlder ? (
        <button
          type="button"
          className="vp-btn"
          data-testid="assistant-load-older"
          style={{ width: '100%', marginTop: 8 }}
          disabled={!canLoadOlder || loadingOlder}
          onClick={onLoadOlder}
        >
          {loadingOlder ? '載入中…' : '載入較早的伺服器訊息'}
        </button>
      ) : null}
      <h3 style={{ marginTop: 18 }}>助理規則</h3>
      {(rules || []).map(([k, v]) => <div className="vp-rule" key={k}><strong>{k}</strong>{v}</div>)}
    </div>
  );
}

function mapServerHistoryMessage(row) {
  if (!row || (row.role !== 'user' && row.role !== 'assistant')) return null;
  const text = typeof row.content === 'string' ? row.content : '';
  if (!text.trim()) return null;
  return {
    id: `srv_${row.id}`,
    role: row.role === 'user' ? 'user' : 'bot',
    text,
    createdAt: row.created_at,
    fromServer: true,
  };
}

function mergeServerConversationList(prev, items) {
  const existing = new Set((prev.sessions || []).map((s) => s.id));
  const additions = [];
  for (const item of Array.isArray(items) ? items : []) {
    const id = typeof item?.conversation_id === 'string' ? item.conversation_id : '';
    if (!id || existing.has(id)) continue;
    const preview = String(item.last_message || '').replace(/\s+/g, ' ').trim().slice(0, 32);
    additions.push({
      id,
      title: preview || '伺服器對話',
      createdAt: item.created_at || new Date().toISOString(),
      updatedAt: item.updated_at || item.created_at || new Date().toISOString(),
      messages: [],
      fromServer: true,
    });
  }
  if (!additions.length) return prev;
  return { ...prev, sessions: [...(prev.sessions || []), ...additions] };
}

export function AssistantSession({ org, sessionsKey, legacyKey }) {
  const { data: status, error: statusError, loading } = useLoad(() => loadAssistantFeatures(), []);
  const sessionsKeyRef = useRef(sessionsKey);
  const [store, setStore] = useState(() => {
    const loaded = loadAssistantStore(sessionsKey, legacyKey);
    seq = maxMsgSeq(currentSession(loaded).messages);
    return loaded;
  });
  const [drawer, setDrawer] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [attachmentsBySession, setAttachmentsBySession] = useState({});
  const [researchBySession, setResearchBySession] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [toolBusy, setToolBusy] = useState('');
  const [historyCursor, setHistoryCursor] = useState({});
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [historyNote, setHistoryNote] = useState('');
  const endRef = useRef(null);
  const activeRequestRef = useRef(null);
  const currentIdRef = useRef(null);
  const current = currentSession(store);
  currentIdRef.current = current.id;
  const msgs = current.messages || [];
  const features = parseAssistantFeatures(status);
  const sessionAttachments = attachmentsBySession[current.id] || [];
  const ownedIds = ownedIdSet(sessionAttachments);
  const researchEnabled = !!researchBySession[current.id];
  const lastBotText = [...msgs].reverse().find((m) => m.role === 'bot' && m.text && !m.error)?.text || '';


  useEffect(() => {
    seq = maxMsgSeq(msgs);
  }, [current.id]);

  useEffect(() => {
    saveAssistantStore(sessionsKey, store);
  }, [sessionsKey, store]);

  // 從其他頁回來時重讀 localStorage（背景請求可能已寫入回覆）
  useEffect(() => {
    const reload = () => {
      const next = loadAssistantStore(sessionsKeyRef.current, legacyKey);
      seq = maxMsgSeq(currentSession(next).messages);
      setStore(next);
      setSending(!!getPendingSend(sessionsKeyRef.current));
    };
    const onVis = () => { if (document.visibilityState === 'visible') reload(); };
    window.addEventListener('focus', reload);
    document.addEventListener('visibilitychange', onVis);
    // 進頁先讀一次（含剛在其他頁完成的回覆）
    reload();
    const poll = setInterval(() => {
      if (!getPendingSend(sessionsKeyRef.current)) return;
      reload();
    }, 2500);
    return () => {
      window.removeEventListener('focus', reload);
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(poll);
    };
  }, [legacyKey, sessionsKey]);

  useEffect(() => {
    const storageKey = sessionsKeyRef.current;
    let cancelled = false;
    (async () => {
      try {
        const result = await listAssistantConversations({ limit: 30 });
        if (cancelled || sessionsKeyRef.current !== storageKey) return;
        const items = Array.isArray(result?.items) ? result.items : [];
        const persistent = status?.features?.history?.persistent === true
          || status?.conversation_history?.persistent === true
          || result?.persistent === true;
        setHistoryNote(persistent
          ? '伺服器對話歷史已載入清單；不會覆蓋目前正在進行的對話。'
          : '已讀到伺服器對話清單；未收到持久化旗標，不會宣稱已完整保存。');
        setStore((prev) => mergeServerConversationList(prev, items));
      } catch (err) {
        if (cancelled || sessionsKeyRef.current !== storageKey) return;
        const s = err?.response?.status;
        if (s === 404 || s === 501) {
          setHistoryNote('這個環境沒有伺服器對話歷史端點，畫面仍使用本機紀錄。');
          return;
        }
        setHistoryNote(assistantToolErrorMessage(err, '伺服器對話清單暫時無法載入。'));
      }
    })();
    return () => { cancelled = true; };
  }, [sessionsKey, status]);

  const enabled = !loading && !statusError && !!status?.enabled;
  const canType = !loading;
  const capabilityCopy = assistantCapabilityCopy(status);
  const disabledReason = loading ? '' : statusError
    ? (isUnavailable(statusError) ? '助理尚未啟用：這個環境還沒有接上助理服務。' : errorMessage(statusError))
    : (!status?.enabled ? (status?.reason || '助理尚未啟用：AI 服務尚未設定。監理站號牌仍可直接詢問。') : '');

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs, sending]);

  const patchMsgs = (updater) => {
    setStore((prev) => {
      const cur = currentSession(prev);
      const nextMsgs = typeof updater === 'function' ? updater(cur.messages || []) : updater;
      return setSessionMessages(prev, cur.id, nextMsgs);
    });
  };

  const applyExtra = (storageKey, sessionId, extra) => {
    if (!extra.length) return;
    const saved = appendSessionMessages(storageKey, sessionId, extra);
    setStore((prev) => {
      if (sessionsKeyRef.current !== storageKey) return prev;
      return saved || loadAssistantStore(storageKey);
    });
  };

  const stillScoped = (storageKey, sessionId) => {
    if (sessionsKeyRef.current !== storageKey) return false;
    const saved = loadAssistantStore(storageKey);
    return (saved.sessions || []).some((s) => s.id === sessionId);
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending || !canType) return;
    setText('');
    const sessionId = current.id;
    const storageKey = sessionsKeyRef.current;
    const requestToken = `request_${Date.now().toString(36)}_${++requestSeq}`;
    activeRequestRef.current = { requestToken, storageKey, sessionId };
    const ownedNow = ownedIdSet(attachmentsBySession[sessionId] || []);
    const attachmentIds = readyAttachmentIds(attachmentsBySession[sessionId] || []);
    const mode = researchEnabled ? 'research' : 'chat';
    const researchResume = researchEnabled && shouldResumeResearch(msgs);
    const userMsg = {
      id: nextId(),
      role: 'user',
      text: t,
      attachments: visibleAttachments(attachmentsBySession[sessionId] || [], ownedNow)
        .filter((file) => attachmentIds.includes(file.id))
        .map((file) => ({ id: file.id, name: file.name, downloadUrl: file.downloadUrl })),
    };
    // 先同步寫入 localStorage，避免換頁太快導致 useEffect 來不及存
    setStore((prev) => {
      const next = setSessionMessages(prev, sessionId, [...(currentSession(prev).messages || []), userMsg]);
      saveAssistantStore(storageKey, next);
      return next;
    });
    setSending(true);
    setPendingSend(storageKey, { sessionId, startedAt: Date.now(), text: t, requestToken });
    // 換頁後 React 會卸載，但請求繼續；結果直接寫 localStorage，回來就能看到
    try {
      const r = (attachmentIds.length || mode === 'research')
        ? await sendAssistantMessageWithTools({
          text: t,
          conversationId: sessionId,
          attachmentIds,
          mode,
          researchResume,
        })
        : await vpApi.sendAssistantMessage(t, sessionId);
      if (r?.conversation_id !== sessionId) {
        throw new Error('助理回覆的對話識別不一致，已停止寫入本機對話。');
      }
      if (!stillScoped(storageKey, sessionId)) return;
      const imported = normalizeUploadedFiles(r?.imported_files, null);
      const responseOwned = new Set([...ownedNow, ...imported.map((file) => file.id)]);
      if (imported.length) {
        setAttachmentsBySession((prev) => {
          const merged = [...(prev[sessionId] || [])];
          imported.forEach((file) => {
            const index = merged.findIndex((item) => item.id === file.id);
            if (index >= 0) merged[index] = file;
            else merged.push(file);
          });
          return { ...prev, [sessionId]: merged };
        });
      }
      const extra = chatItemsFromResult(r).map((item) => {
        const enriched = enrichChatItemWithTools(item, r, responseOwned);
        return {
          id: nextId(),
          ...enriched,
          text: stripUnauthorizedIdsFromText(enriched.text, collectFileIds(r), responseOwned),
        };
      });
      applyExtra(storageKey, sessionId, extra);
    } catch (err) {
      if (!stillScoped(storageKey, sessionId)) return;
      applyExtra(storageKey, sessionId, [{
        id: nextId(),
        role: 'bot',
        error: true,
        text: stripUnauthorizedIdsFromText(
          `訊息沒有送達：${assistantToolErrorMessage(err, errorMessage(err, '助理暫時無法回應。'))}沒有任何資料被變更。`,
          attachmentIds,
          ownedNow
        ),
      }]);
    } finally {
      clearPendingSend(storageKey, requestToken);
      const active = activeRequestRef.current;
      if (active?.requestToken === requestToken && sessionsKeyRef.current === storageKey) {
        activeRequestRef.current = null;
        setSending(false);
      }
    }
  };

  const updateDraft = (msgId, draft) => patchMsgs((m) => m.map((x) => (x.id === msgId ? { ...x, draft } : x)));

  const handleNew = () => {
    setStore((prev) => createSession(prev));
    setDrawer(false);
  };

  const handleSwitch = (id) => {
    const target = (store.sessions || []).find((s) => s.id === id);
    setStore((prev) => switchSession(prev, id));
    setDrawer(false);
    if (target && !(target.messages || []).length) {
      (async () => {
        const storageKey = sessionsKeyRef.current;
        try {
          const result = await listAssistantConversationMessages(id, { limit: 50 });
          if (!stillScoped(storageKey, id) || currentIdRef.current !== id) return;
          const mapped = (Array.isArray(result?.items) ? result.items : [])
            .map(mapServerHistoryMessage).filter(Boolean).reverse();
          setStore((prev) => {
            const cur = (prev.sessions || []).find((s) => s.id === id);
            if (!cur || (cur.messages || []).length) return prev;
            if (!mapped.length) return prev;
            return setSessionMessages(prev, id, mapped);
          });
          setHistoryCursor((prev) => ({ ...prev, [id]: result?.next_cursor || null }));
        } catch {
          /* 端點不存在或無權限時維持空白本機會話，不覆蓋 */
        }
      })();
    }
  };

  const handleRename = (id, currentTitle) => {
    const next = window.prompt('對話名稱', currentTitle || '');
    if (next == null) return;
    setStore((prev) => renameSession(prev, id, next));
  };

  const handleDelete = (id) => {
    if (!window.confirm('確定刪除這則對話？刪除後無法復原。')) return;
    setStore((prev) => deleteSession(prev, id));
    setAttachmentsBySession((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setResearchBySession((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleUpload = async (fileList) => {
    if (!fileList?.length || !features.files.available) return;
    const sessionId = current.id;
    const storageKey = sessionsKeyRef.current;
    setUploading(true);
    setUploadError('');
    try {
      const { uploaded, failures } = await uploadAssistantFiles(fileList, {
        maxBytes: features.files.maxUploadBytes,
      });
      if (!stillScoped(storageKey, sessionId)) return;
      const normalized = uploaded.flatMap((payload) => normalizeUploadedFiles(payload, null));
      if (normalized.length) {
        setAttachmentsBySession((prev) => {
          const currentFiles = prev[sessionId] || [];
          const merged = [...currentFiles];
          normalized.forEach((file) => {
            if (!file?.id) return;
            const idx = merged.findIndex((x) => x.id === file.id);
            if (idx >= 0) merged[idx] = file;
            else merged.push(file);
          });
          return { ...prev, [sessionId]: merged };
        });
      }
      if (failures.length) {
        const names = failures.map((item) => item.name).join('、');
        const first = assistantToolErrorMessage(failures[0].error, '檔案沒有上傳成功。');
        setUploadError(normalized.length
          ? `部分檔案已上傳。未成功：${names}。${first}`
          : `${first}${names ? `（${names}）` : ''}`);
      }
    } catch (err) {
      if (!stillScoped(storageKey, sessionId)) return;
      setUploadError(assistantToolErrorMessage(err, '檔案沒有上傳成功。'));
    } finally {
      if (sessionsKeyRef.current === storageKey) setUploading(false);
    }
  };

  const handleDownload = async (file) => {
    const sessionId = current.id;
    const orgId = org?.id;
    const path = file?.downloadUrl || assistantFileDownloadPath(file?.id);
    try {
      await downloadAssistantBlob({
        path,
        filename: file?.name,
        orgId,
        conversationId: sessionId,
        getLiveConversationId: () => currentIdRef.current,
      });
    } catch (err) {
      setUploadError(assistantToolErrorMessage(err, '檔案沒有下載成功。'));
    }
  };

  const handleLoadOlder = async () => {
    const sessionId = current.id;
    const storageKey = sessionsKeyRef.current;
    const cursor = historyCursor[sessionId];
    setLoadingOlder(true);
    try {
      const result = await listAssistantConversationMessages(sessionId, {
        limit: 50,
        before: cursor || undefined,
      });
      if (!stillScoped(storageKey, sessionId) || currentIdRef.current !== sessionId) return;
      const items = Array.isArray(result?.items) ? result.items : [];
      const mapped = items.map(mapServerHistoryMessage).filter(Boolean).reverse();
      if (mapped.length) {
        setStore((prev) => {
          const cur = currentSession(prev);
          if (cur.id !== sessionId) return prev;
          const existing = new Set((cur.messages || []).map((m) => m.id));
          const extra = mapped.filter((m) => !existing.has(m.id));
          if (!extra.length) return prev;
          return setSessionMessages(prev, sessionId, [...extra, ...(cur.messages || [])]);
        });
      }
      setHistoryCursor((prev) => ({ ...prev, [sessionId]: result?.next_cursor || null }));
    } catch (err) {
      if (!stillScoped(storageKey, sessionId)) return;
      setUploadError(assistantToolErrorMessage(err, '較早的對話訊息沒有載入。'));
    } finally {
      if (sessionsKeyRef.current === storageKey) setLoadingOlder(false);
    }
  };

  const handleRemoveAttachment = (fileId) => {
    const sessionId = current.id;
    setAttachmentsBySession((prev) => ({
      ...prev,
      [sessionId]: (prev[sessionId] || []).filter((file) => file.id !== fileId),
    }));
  };

  const runTool = async (kind, sessionId, work, toItems) => {
    const storageKey = sessionsKeyRef.current;
    const requestToken = `request_${Date.now().toString(36)}_${++requestSeq}`;
    setToolBusy(kind);
    try {
      const payload = await work();
      if (!stillScoped(storageKey, sessionId)) return;
      if (payload?.conversation_id && payload.conversation_id !== sessionId) return;
      const extra = toItems(payload).map((item) => ({ id: nextId(), ...item }));
      applyExtra(storageKey, sessionId, extra);
    } catch (err) {
      if (!stillScoped(storageKey, sessionId)) return;
      applyExtra(storageKey, sessionId, [{
        id: nextId(),
        role: 'bot',
        error: true,
        text: `${assistantToolErrorMessage(err, '這項操作沒有完成。')}沒有任何資料被變更。`,
      }]);
    } finally {
      if (activeRequestRef.current?.requestToken !== requestToken && sessionsKeyRef.current === storageKey) {
        setToolBusy('');
      } else if (sessionsKeyRef.current === storageKey) {
        setToolBusy('');
      }
    }
  };

  const handleCreateArtifact = ({ format, title, content, filename }) => {
    const sessionId = current.id;
    const ownedNow = ownedIdSet(attachmentsBySession[sessionId] || []);
    return runTool('artifact', sessionId, () => createAssistantArtifact({
      format, title, content, filename, conversation_id: sessionId,
    }), (payload) => toolItemsFromIndependentResult('artifact', payload, ownedNow));
  };

  const handleRunAnalysis = ({ fileId, operation, columns }) => {
    const sessionId = current.id;
    const ownedNow = ownedIdSet(attachmentsBySession[sessionId] || []);
    if (!ownedNow.has(fileId)) {
      setUploadError('只能分析目前對話已上傳且屬於你的檔案。');
      return;
    }
    return runTool('analysis', sessionId, () => runAssistantAnalysis({
      file_id: fileId,
      operation,
      columns: columns.length ? columns : undefined,
      conversation_id: sessionId,
    }), (payload) => toolItemsFromIndependentResult('analysis', payload, ownedNow));
  };

  const handleGenerateImage = ({ prompt, confirmed }) => {
    if (!features.images.available) return;
    if (confirmed !== true) return;
    const sessionId = current.id;
    const ownedNow = ownedIdSet(attachmentsBySession[sessionId] || []);
    return runTool('image', sessionId, () => generateAssistantImage({
      prompt,
      confirmed: true,
      conversation_id: sessionId,
    }), (payload) => toolItemsFromIndependentResult('image', payload, ownedNow));
  };

  return (
    <div className="vp-asst">
      <SessionSidebar
        store={store}
        rules={capabilityCopy.rules}
        onNew={handleNew}
        onSwitch={handleSwitch}
        onRename={handleRename}
        onDelete={handleDelete}
        onLoadOlder={handleLoadOlder}
        canLoadOlder={historyCursor[current.id] !== null}
        loadingOlder={loadingOlder}
        historyNote={historyNote}
      />
      <div className="vp-chat">
        <div className="vp-asst-mobilebar">
          <button type="button" className="vp-btn" onClick={() => setDrawer(true)}>對話紀錄</button>
          <button type="button" className="vp-btn primary" onClick={handleNew}>新對話</button>
          <span className="vp-muted" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current.title}</span>
        </div>
        <div className="vp-msgs" aria-live="polite">
          <div className="vp-bubble bot">{capabilityCopy.intro}</div>
          {disabledReason && <div className="vp-warnbox" style={{ marginTop: 0, maxWidth: 640 }}>{disabledReason}</div>}
          {msgs.map((m) => {
            if (m.role === 'draft') return <DraftCard key={m.id} draft={m.draft} onChange={(d) => updateDraft(m.id, d)} />;
            const webSources = sanitizeWebSources(m.webSources);
            const searchMeta = searchMetaCopy({ ...m, webSources });
            return (
              <div key={m.id} className={`vp-bubble ${m.role}`} style={m.error ? { background: '#fef2f2', color: '#991b1b' } : undefined}>
                {m.text}
                {m.role === 'user' && m.attachments?.length ? (
                  <div className="vp-asst-user-files">
                    {m.attachments.map((file) => (
                      <button
                        type="button"
                        key={file.id || file.name}
                        className="vp-asst-download"
                        data-testid="assistant-user-file-download"
                        data-href={file.downloadUrl || ''}
                        data-name={file.name}
                        onClick={() => handleDownload(file)}
                      >
                        {file.name}
                      </button>
                    ))}
                  </div>
                ) : null}
                {m.sources?.length > 0 && <div className="vp-sources">{m.sources.map((s) => <Link key={s.link} to={s.link}>{s.label}</Link>)}</div>}
                {webSources.length > 0 && (
                  <div className="vp-sources vp-web-sources">
                    外部來源：{webSources.map((s) => {
                      const note = verificationLabel(s.verification);
                      return (
                        <span className="vp-web-source" key={s.url}>
                          <a href={s.url} target="_blank" rel="noopener noreferrer">{s.title || s.url}</a>
                          {note ? <span className="vp-source-verify">{note}</span> : null}
                        </span>
                      );
                    })}
                  </div>
                )}
                {searchMeta ? <div className="vp-search-meta">{searchMeta}</div> : null}
                {m.contextNotice ? <div className="vp-search-meta">{m.contextNotice}</div> : null}
                <AssistantMessageExtras message={m} onDownload={handleDownload} />
              </div>
            );
          })}
          {sending && <div className="vp-bubble bot vp-muted" role="status">助理思考中…（可先去其他頁，回來會看到回覆）</div>}
          <div ref={endRef} />
        </div>
        <form className="vp-asst-composer-wrap" onSubmit={send}>
          <AssistantComposerTools
            features={features}
            attachments={sessionAttachments}
            ownedIds={ownedIds}
            researchEnabled={researchEnabled}
            onToggleResearch={(on) => setResearchBySession((prev) => ({ ...prev, [current.id]: on }))}
            onUpload={handleUpload}
            onRemoveAttachment={handleRemoveAttachment}
            onDownloadAttachment={handleDownload}
            uploading={uploading}
            uploadError={uploadError}
            onCreateArtifact={handleCreateArtifact}
            onRunAnalysis={handleRunAnalysis}
            onGenerateImage={handleGenerateImage}
            busy={toolBusy}
            lastBotText={lastBotText}
            disabled={!canType}
          />
          <div className="vp-composer">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={!canType}
              placeholder={enabled ? '輸入問題或指示… 例如：查臺北市今天自用小客車可領號牌' : 'AI 未啟用。仍可問監理站號牌，例如：查臺北市今天自用小客車可領號牌'}
              aria-label="輸入給助理的訊息"
            />
            <button type="submit" disabled={!canType || sending || !text.trim()}>送出</button>
          </div>
        </form>
        <p className="vp-chat-hint">
          <span>規則：助理的寫入一律先產生草稿，經你確認後才會生效 · 目前組織：{org?.name} · {current.title}</span>
        </p>
      </div>
      {drawer && (
        <div className="vp-session-drawer" role="dialog" aria-label="對話紀錄">
          <button type="button" className="vp-session-scrim" aria-label="關閉" onClick={() => setDrawer(false)} />
          <SessionSidebar
            store={store}
            rules={capabilityCopy.rules}
            onNew={handleNew}
            onSwitch={handleSwitch}
            onRename={handleRename}
            onDelete={handleDelete}
            onLoadOlder={handleLoadOlder}
            canLoadOlder={historyCursor[current.id] !== null}
            loadingOlder={loadingOlder}
            historyNote={historyNote}
            onClose={() => setDrawer(false)}
          />
        </div>
      )}
    </div>
  );
}

export default function AssistantPage() {
  return <RequireOrg><AssistantScope /></RequireOrg>;
}

function AssistantScope() {
  const org = useOrgStore((s) => s.currentOrg);
  const user = useAuthStore((s) => s.user);
  const sessionsKey = assistantSessionsKey(org?.id, user?.id);
  const legacyKey = assistantMsgsKey(org?.id, user?.id);
  return (
    <AssistantSession
      key={sessionsKey}
      org={org}
      sessionsKey={sessionsKey}
      legacyKey={legacyKey}
    />
  );
}
